declare global {
  interface Window {
    AwsWafIntegration?: {
      fetch: typeof window.fetch;
      getToken: () => Promise<string>;
    };
  }
}

const CHALLENGE_SCRIPT_URL = import.meta.env.VITE_AWS_WAF_CHALLENGE_URL;

let scriptLoadPromise: Promise<void> | null = null;

export function loadWafScript(): Promise<void> {
  if (window.AwsWafIntegration) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CHALLENGE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load AWS WAF challenge script"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

/**
 * Drop-in replacement for fetch() that silently acquires and attaches
 * an AWS WAF challenge token. WAF validates the token at the edge
 * before the request reaches the backend.
 *
 * A 202 response means WAF intercepted the request before the token
 * was ready -- this should not happen in normal flow since loadWafScript()
 * is called on app mount, but we throw explicitly so it surfaces as an
 * error rather than a silent empty success.
 */
export async function wafFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  await loadWafScript();

  if (!window.AwsWafIntegration) {
    console.warn("AWS WAF Integration not available, falling back to regular fetch");
    return fetch(input, init);
  }

  return await window.AwsWafIntegration.fetch(input, init);
}
